
import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function StressAssessment() {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<{[key: string]: number}>({});
  const [showResult, setShowResult] = useState(false);

  const questions = [
    {
      id: 'q1',
      text: '最近、イライラしやすいと感じますか？',
      category: '感情面'
    },
    {
      id: 'q2', 
      text: '疲れが取れにくいと感じますか？',
      category: '身体面'
    },
    {
      id: 'q3',
      text: '集中力が続かないことがありますか？',
      category: '認知面'
    },
    {
      id: 'q4',
      text: '人との関わりを避けたくなりますか？',
      category: '行動面'
    },
    {
      id: 'q5',
      text: '食欲に変化がありますか？',
      category: '身体面'
    },
    {
      id: 'q6',
      text: '将来に対して不安を感じますか？',
      category: '感情面'
    },
    {
      id: 'q7',
      text: '物事を決めるのに時間がかかりますか？',
      category: '認知面'
    },
    {
      id: 'q8',
      text: '趣味や楽しみに興味がなくなりましたか？',
      category: '行動面'
    }
  ];

  const handleAnswer = (questionId: string, score: number) => {
    setAnswers(prev => ({...prev, [questionId]: score}));
  };

  const calculateResult = () => {
    const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
    const maxScore = questions.length * 4;
    const percentage = (totalScore / maxScore) * 100;
    
    if (percentage <= 25) return { level: '低', color: 'green', message: 'ストレスレベルは低めです。現在の生活習慣を維持しましょう。' };
    if (percentage <= 50) return { level: '中', color: 'yellow', message: 'ストレスレベルは中程度です。リラクゼーション法を取り入れることをお勧めします。' };
    if (percentage <= 75) return { level: '高', color: 'orange', message: 'ストレスレベルが高めです。専門家への相談を検討してください。' };
    return { level: '非常に高', color: 'red', message: 'ストレスレベルが非常に高いです。早めに専門医にご相談ください。' };
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length === questions.length) {
      setShowResult(true);
    }
  };

  const resetAssessment = () => {
    setCurrentStep(1);
    setAnswers({});
    setShowResult(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 cursor-pointer">ホーム</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li>
                <Link to="/health-support" className="text-gray-500 hover:text-gray-700 cursor-pointer">健康サポート</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-gray-400"></i>
              </li>
              <li className="text-gray-900 font-medium">ストレス評価</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/db86e8654fabf43f04b68c33c4b5dd01.jpg"
            alt="ストレス評価"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            ストレス<span className="text-blue-600">評価</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            心理的ストレスの状態を科学的に評価し、適切な対処法をご提案します
          </p>
        </div>
      </section>

      {/* Assessment Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!showResult ? (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">ストレス評価チェック</h2>
                  <div className="text-sm text-gray-500">
                    {Object.keys(answers).length} / {questions.length} 完了
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(Object.keys(answers).length / questions.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-8">
                {questions.map((question, index) => (
                  <div key={question.id} className="border-b border-gray-200 pb-6">
                    <div className="mb-4">
                      <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mb-2">
                        {question.category}
                      </span>
                      <h3 className="text-lg font-medium text-gray-900">
                        {index + 1}. {question.text}
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { value: 1, label: 'まったくない' },
                        { value: 2, label: 'たまにある' },
                        { value: 3, label: 'よくある' },
                        { value: 4, label: 'いつもある' }
                      ].map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleAnswer(question.id, option.value)}
                          className={`p-3 rounded-lg border text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                            answers[question.id] === option.value
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 text-center">
                <button
                  onClick={handleSubmit}
                  disabled={Object.keys(answers).length !== questions.length}
                  className={`px-8 py-3 rounded-lg font-semibold whitespace-nowrap cursor-pointer ${
                    Object.keys(answers).length === questions.length
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  結果を確認する
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">評価結果</h2>
                <div className={`inline-block px-6 py-3 rounded-full text-lg font-bold ${
                  calculateResult().color === 'green' ? 'bg-green-100 text-green-800' :
                  calculateResult().color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                  calculateResult().color === 'orange' ? 'bg-orange-100 text-orange-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  ストレスレベル: {calculateResult().level}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <p className="text-gray-700 text-center text-lg">
                  {calculateResult().message}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    <i className="ri-lightbulb-line text-blue-600 mr-2"></i>
                    おすすめの対処法
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• 深呼吸やリラクゼーション法の実践</li>
                    <li>• 規則正しい睡眠リズムの確立</li>
                    <li>• 適度な運動習慣の導入</li>
                    <li>• 信頼できる人との会話</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    <i className="ri-heart-line text-green-600 mr-2"></i>
                    関連サービス
                  </h3>
                  <div className="space-y-3">
                    <Link to="/mental-health/relaxation-guidance" className="block text-green-600 hover:text-green-700 cursor-pointer">
                      → リラクゼーション指導
                    </Link>
                    <Link to="/mental-health/sleep-improvement-support" className="block text-green-600 hover:text-green-700 cursor-pointer">
                      → 睡眠改善サポート
                    </Link>
                    <Link to="/mental-health/specialist-collaboration" className="block text-green-600 hover:text-green-700 cursor-pointer">
                      → 専門医連携
                    </Link>
                  </div>
                </div>
              </div>

              <div className="text-center space-x-4">
                <button
                  onClick={resetAssessment}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 whitespace-nowrap cursor-pointer"
                >
                  再度評価する
                </button>
                <Link
                  to="/health-support"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 whitespace-nowrap cursor-pointer"
                >
                  健康サポートに戻る
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">メンタルヘルス</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/mental-health/stress-assessment" className="hover:text-white cursor-pointer">ストレス評価</Link></li>
                <li><Link to="/mental-health/relaxation-guidance" className="hover:text-white cursor-pointer">リラクゼーション指導</Link></li>
                <li><Link to="/mental-health/sleep-improvement-support" className="hover:text-white cursor-pointer">睡眠改善サポート</Link></li>
                <li><Link to="/mental-health/specialist-collaboration" className="hover:text-white cursor-pointer">専門医連携</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">健康サポート</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康相談</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">サルコペニア予防</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">フレイル対策</Link></li>
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">栄養指導</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">ビースト君ブログ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
